/**
 * Ticket KI1, Teil 2: Maschinenlesbare Angaben für Mentorings und Keynotes.
 *
 * Bewusst reine Logik – keine Datenbank, kein React. Die Routen setzen das
 * Ergebnis über `head().scripts` in den Seitenkopf, genau wie die Blogbeiträge.
 *
 * Vokabular und Begründung:
 *  – Mentoring: `Service` mit eingebettetem `Offer`. Ein Mentoring ist eine
 *    dauerhaft buchbare Leistung, keine Ware; ein `Offer` allein wäre ein Preis
 *    ohne Gegenstand. Betrag und Währung stehen nur im `Offer` maschinell
 *    prüfbar.
 *  – Betreuung: `Person` als `provider` des `Service`.
 *  – Häufige Fragen: `FAQPage`, nur wenn Fragen vorhanden sind.
 *  – Übersicht: `ItemList` in der redaktionellen Reihenfolge.
 *  – Keynotes: `ItemList` von `Person`. Ausdrücklich KEIN `Event`: es steht
 *    weder Termin noch Ort fest, ein Datum wäre erfunden.
 *
 * Fehlt ein Preis, entfällt der `Offer`-Teil – kein Platzhalterbetrag.
 */

import type { MentoringCard, MentoringPage } from "@/lib/mentorings";
import type { KeynoteSpeaker } from "@/lib/keynotes";
import { KEYNOTE_PAGE_PATH } from "@/lib/keynotes";
import { MENTORING_PREFIX } from "@/lib/mentorings";

export const SITE_ORIGIN = "https://creategy.de";
export const MENTORING_LIST_PATH = MENTORING_PREFIX;

export type JsonLd = Record<string, unknown>;

export function siteUrl(path: string): string {
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

export function mentoringUrl(slug: string): string {
  return siteUrl(`${MENTORING_PREFIX}/${slug}`);
}

/**
 * Bruttobetrag aus dem gepflegten Preistext, z. B. „4.890 €" → "4890.00".
 * Ohne erkennbare Zahl: null – dann entfällt der Preisteil.
 */
export function parsePriceAmount(label: string | null | undefined): string | null {
  const text = (label ?? "").trim();
  if (!text) return null;
  const match = text.match(/\d[\d.\s]*(?:,\d{1,2})?/);
  if (!match) return null;
  const normalized = match[0].replace(/[.\s]/g, "").replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value.toFixed(2);
}

function personJsonLd(person: {
  name: string;
  jobTitle?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
}): JsonLd {
  const node: JsonLd = { "@type": "Person", name: person.name };
  const jobTitle = (person.jobTitle ?? "").trim();
  if (jobTitle) node.jobTitle = jobTitle;
  const bio = (person.bio ?? "").trim();
  if (bio) node.description = bio;
  const photo = (person.photoUrl ?? "").trim();
  if (photo.startsWith("https://")) node.image = photo;
  return node;
}

/** Mentoring-Detailseite: Leistung mit Preis und betreuender Person. */
export function mentoringServiceJsonLd(mentoring: MentoringPage): JsonLd {
  const url = mentoringUrl(mentoring.slug);
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: mentoring.title,
    serviceType: "Mentoring für politische Kommunikation",
    url,
    provider: { "@type": "Organization", name: "creategy GmbH", url: SITE_ORIGIN },
  };
  const description = (mentoring.shortDescription ?? "").trim();
  if (description) node.description = description;
  if (mentoring.previewImageUrl?.startsWith("https://")) node.image = mentoring.previewImageUrl;

  const price = parsePriceAmount(mentoring.priceLabel);
  if (price) {
    node.offers = {
      "@type": "Offer",
      price,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url,
    };
  }
  if (mentoring.mentor) {
    node.provider = personJsonLd(mentoring.mentor);
  }
  return node;
}

/** Häufige Fragen einer Mentoring-Seite; ohne Fragen: null. */
export function mentoringFaqJsonLd(mentoring: MentoringPage): JsonLd | null {
  const faqs = (mentoring.faqs ?? []).filter(
    (item) => item.question.trim() && (item.answer ?? "").trim(),
  );
  if (faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question.trim(),
      acceptedAnswer: { "@type": "Answer", text: (item.answer ?? "").trim() },
    })),
  };
}

/** Mentoring-Übersicht: Liste der Angebote in redaktioneller Reihenfolge. */
export function mentoringListJsonLd(mentorings: MentoringCard[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Mentorings von creategy",
    url: siteUrl(MENTORING_LIST_PATH),
    itemListElement: mentorings.map((mentoring, index) => {
      const item: JsonLd = {
        "@type": "Service",
        name: mentoring.title,
        url: mentoringUrl(mentoring.slug),
      };
      const price = parsePriceAmount(mentoring.priceLabel);
      if (price) {
        item.offers = { "@type": "Offer", price, priceCurrency: "EUR" };
      }
      return { "@type": "ListItem", position: index + 1, item };
    }),
  };
}

/**
 * Keynote-Seite: die Referentinnen und Referenten als Personen.
 * Die Themen bleiben Text – kein `Event`, weil Termin und Ort offen sind.
 */
export function keynoteSpeakersJsonLd(speakers: KeynoteSpeaker[]): JsonLd | null {
  if (speakers.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Keynotes und Vorträge von creategy",
    url: siteUrl(KEYNOTE_PAGE_PATH),
    itemListElement: speakers.map((speaker, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: personJsonLd(speaker.person),
    })),
  };
}

/** Kurzform für den Einbau in `head().scripts`. */
export function jsonLdScript(node: JsonLd | null): Array<{ type: string; children: string }> {
  if (!node) return [];
  return [{ type: "application/ld+json", children: JSON.stringify(node) }];
}
